import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineFamilyRestroom,
  MdOutlinePerson,
  MdWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_ACTIVITY_STATUS,
  ACORIS_FAMILY_SITUATION,
  ACORIS_PROFESSIONS,
  ACORIS_PROFILES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Activity = ({ setPercent, disabled, initialValues, bindField }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: processesT } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const ACORIS_TRANSLATIONS = processesT("B2C_ACORIS", {
    returnObjects: true,
  });

  const profileType =
    get(initialValues, "project.contact.fields.profile") ||
    get(initialValues, "project.fields.profileType");

  const activityStatus =
    get(initialValues, "project.contact.fields.status") ||
    get(initialValues, "project.contact.fields.activityStatus");

  const profileOptions = useMemo(
    () =>
      Object.entries(ACORIS_PROFILES).map(([key, value]) => ({
        label: ACORIS_TRANSLATIONS.PROFILE_TYPES?.[key] || value,
        value,
      })),
    [ACORIS_TRANSLATIONS.PROFILE_TYPES],
  );

  const statusOptions = useMemo(
    () =>
      Object.entries(ACORIS_ACTIVITY_STATUS).map(([key, value]) => ({
        label: ACORIS_TRANSLATIONS.ACTIVITY_STATUS?.[key] || value,
        value,
      })),
    [ACORIS_TRANSLATIONS.ACTIVITY_STATUS],
  );

  const professionOptions = useMemo(
    () =>
      Object.entries(ACORIS_PROFESSIONS).map(([key, value]) => ({
        label: ACORIS_TRANSLATIONS.PROFESSIONS?.[key] || value,
        value,
      })),
    [ACORIS_TRANSLATIONS.PROFESSIONS],
  );

  const familySituationOptions = useMemo(
    () =>
      Object.entries(ACORIS_FAMILY_SITUATION).map(([key, value]) => ({
        label: ACORIS_TRANSLATIONS.FAMILY_SITUATION?.[key] || value,
        value,
      })),
    [ACORIS_TRANSLATIONS.FAMILY_SITUATION],
  );

  const statusPath = "project.contact.fields.status";
  const statusField = bindField(statusPath, {
    label: SUBSCRIPTION.FORM.ACTIVITY_STATUS?.LABEL || "Statut",
    required: true,
    onChange: (value) => {
      if (value !== ACORIS_ACTIVITY_STATUS.INDEPENDANT) {
        set(initialValues, "project.contact.optMadelin", undefined);
      }
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const professionPath = "project.contact.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: true,
    disabled: get(disabled, "project.contact.profession"),
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const familySituationPath = "project.contact.familySituation";
  const familySituationField = bindField(familySituationPath, {
    label: SUBSCRIPTION.FORM.FAMILY_SITUATION.LABEL,
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Select
              icon={<MdOutlinePerson size={16} />}
              label={SUBSCRIPTION.FORM.ACORIS_PROFILE?.LABEL || "Profil"}
              placeholder={
                SUBSCRIPTION.FORM.ACORIS_PROFILE?.PLACEHOLDER ||
                "Profil du prospect"
              }
              options={profileOptions}
              defaultValue={profileType}
              value={profileType}
              disabled={get(disabled, "project.contact.fields.profile")}
              optional
            />
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={statusField.path}
              label={statusField.label}
              required={statusField.required}
              disabled={statusField.disabled}
            >
              {(field) => (
                <Select
                  options={statusOptions}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.ACTIVITY_STATUS?.PLACEHOLDER}
                  label={statusField.label}
                  defaultValue={activityStatus}
                  {...field}
                  onChange={statusField.onChange}
                  onBlur={statusField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={professionField.path}
              label={professionField.label}
              required={professionField.required}
              disabled={professionField.disabled}
            >
              {(field) => (
                <Select
                  options={professionOptions}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                  label={professionField.label}
                  defaultValue={professionField.defaultValue}
                  {...field}
                  onChange={professionField.onChange}
                  onBlur={professionField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={familySituationField.path}
              label={familySituationField.label}
              required={familySituationField.required}
              disabled={familySituationField.disabled}
            >
              {(field) => (
                <Select
                  options={familySituationOptions}
                  icon={<MdOutlineFamilyRestroom size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FAMILY_SITUATION.PLACEHOLDER}
                  label={familySituationField.label}
                  type="simple"
                  defaultValue={familySituationField.defaultValue}
                  {...field}
                  onChange={familySituationField.onChange}
                  onBlur={familySituationField.onBlur}
                />
              )}
            </FormField>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
